import { TranscriptEntity } from '../models/help-chat.models';

/**
 * C2 / Vertragspunkt 4 — den erkannten Namen im Sprach-Entwurf markieren.
 *
 * Vor einer Schreibaktion ist die entscheidende Information nicht, *dass* etwas verstanden
 * wurde, sondern **welcher Name**. Eigennamen sind die anfälligste Stelle jeder
 * Spracherkennung; „Trag Kerstin ein" und „Trag Kersten ein" klingen gleich und meinen
 * verschiedene Menschen.
 *
 * ## Zwei Quellen, eine Anzeige
 *
 * **Sauber:** Das Backend liefert die aufgelösten Entitäten mit
 * `POST /Chatbot/Transcribe` (`entities: [{ start, length, kind, displayName, id }]`) —
 * dann steht die Markierung auf dem, was der `IEntityResolver` wirklich verstanden hat.
 *
 * **Zwischenlösung, bis es so weit ist:** Wir gleichen das Transkript gegen die ohnehin
 * gecachte `employeeNameList` ab. Das ist unschärfer als der Resolver — es findet keine
 * Kurzformen und keine Beugungen —, zeigt aber genau das, worauf es ankommt. Und es
 * behauptet nichts: Was nicht sicher ein Name aus dem eigenen Betrieb ist, bleibt
 * unmarkiert.
 *
 * Sobald das Backend liefert, gewinnt seine Angabe ({@link resolveTranscriptMarks}), und
 * dieser Abgleich läuft gar nicht erst.
 */

/** Ein Stück Text — markiert oder nicht. Genau das rendert das Template. */
export interface TranscriptSegment {
  text: string;
  /** true = erkannter Name/Abteilung, wird hervorgehoben. */
  marked: boolean;
}

/**
 * Zerlegt das Transkript in markierte und unmarkierte Stücke.
 *
 * @param text     Das Transkript, wie es zurückkam.
 * @param entities Vom Backend gelieferte Entitäten (Vertragspunkt 4). Leer = Rückfall.
 * @param known    Bekannte Anzeigenamen (Mitarbeiter, Abteilungen) für den Rückfall.
 */
export function resolveTranscriptMarks(
  text: string,
  entities: TranscriptEntity[],
  known: readonly string[],
): TranscriptSegment[] {
  const spans = entities.length
    ? entities.map((e) => ({ start: e.start, end: e.start + e.length }))
    : findKnownNames(text, known);
  return splitBySpans(text, spans);
}

/**
 * Findet bekannte Namen im Text — ganze Wörter, längster Treffer zuerst.
 *
 * „Längster zuerst" ist tragend: Steht „Kerstin Below" in der Liste und „Kerstin" auch,
 * soll der volle Name markiert werden, nicht nur der Vorname. Sonst sähe der Nutzer eine
 * halbe Bestätigung.
 */
function findKnownNames(text: string, known: readonly string[]): Span[] {
  const kandidaten = [...known]
    .map((n) => n.trim())
    .filter((n) => n.length >= 3)
    .sort((a, b) => b.length - a.length);

  const spans: Span[] = [];
  const lower = text.toLowerCase();
  for (const name of kandidaten) {
    const needle = name.toLowerCase();
    let from = 0;
    for (;;) {
      const idx = lower.indexOf(needle, from);
      if (idx < 0) break;
      const ende = idx + needle.length;
      // Ganze Wörter: „Anna" darf nicht in „Annahme" treffen.
      const davor = idx === 0 || !isWortzeichen(lower[idx - 1]);
      const danach = ende >= lower.length || !isWortzeichen(lower[ende]);
      if (davor && danach && !overlaps(spans, idx, ende)) {
        spans.push({ start: idx, end: ende });
      }
      from = ende;
    }
  }
  return spans;
}

interface Span {
  start: number;
  end: number;
}

/** Buchstaben, Ziffern und die deutschen Sonderzeichen zählen als Wortinneres. */
function isWortzeichen(c: string): boolean {
  return /[\p{L}\p{N}]/u.test(c);
}

function overlaps(spans: Span[], start: number, end: number): boolean {
  return spans.some((s) => start < s.end && end > s.start);
}

/**
 * Text an den Spans aufteilen.
 *
 * Spans werden sortiert und auf den Textbereich beschnitten — eine Markierung ausserhalb
 * des Textes (fehlerhafte Backend-Angabe) würde sonst leere oder falsche Stücke erzeugen.
 */
function splitBySpans(text: string, spans: Span[]): TranscriptSegment[] {
  if (!text) return [];
  const gueltig = spans
    .map((s) => ({ start: Math.max(0, s.start), end: Math.min(text.length, s.end) }))
    .filter((s) => s.end > s.start)
    .sort((a, b) => a.start - b.start);

  if (!gueltig.length) return [{ text, marked: false }];

  const out: TranscriptSegment[] = [];
  let cursor = 0;
  for (const s of gueltig) {
    if (s.start < cursor) continue; // Überlappung: erste Markierung gewinnt.
    if (s.start > cursor) out.push({ text: text.slice(cursor, s.start), marked: false });
    out.push({ text: text.slice(s.start, s.end), marked: true });
    cursor = s.end;
  }
  if (cursor < text.length) out.push({ text: text.slice(cursor), marked: false });
  return out;
}
